/* File: FormRunner.jsx
 * Path: ananunadm/src/components/Forms/FormRunner.jsx
 *
 * The respondent-facing form engine, shared by the internal answer page, the
 * builder preview and the public page. Handles multi-step navigation, progress,
 * the answer-duration timer, client-side required checks and conditional
 * visibility. Submission is delegated to the caller via `onSubmit(payload)`,
 * so the server stays the single source of truth for validation.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Button, Group, Progress, Stack, Text, Title, Card, Badge, Divider, Alert
} from "@mantine/core";
import { IconClock, IconAlertCircle } from "@tabler/icons-react";
import QuestionRenderer from "./QuestionRenderer";
import { pickLocale, tr, LAYOUT_TYPES } from "../../pages/Forms/formsShared";

function evalCondition(cond, answers) {
	const v = answers[cond.questionKey];
	switch (cond.operator) {
		case "equals": return String(v) === String(cond.value);
		case "notEquals": return String(v) !== String(cond.value);
		case "contains": return Array.isArray(v) ? v.includes(cond.value) : String(v || "").includes(cond.value);
		case "greaterThan": return Number(v) > Number(cond.value);
		case "lessThan": return Number(v) < Number(cond.value);
		case "includes": return Array.isArray(v) && v.includes(cond.value);
		case "isEmpty": return v == null || v === "" || (Array.isArray(v) && v.length === 0);
		case "isNotEmpty": return !(v == null || v === "" || (Array.isArray(v) && v.length === 0));
		default: return true;
	}
}

function isVisible(q, answers) {
	if (!q.visibility?.enabled || !(q.visibility.conditions || []).length) return true;
	const results = q.visibility.conditions.map((c) => evalCondition(c, answers));
	return q.visibility.match === "any" ? results.some(Boolean) : results.every(Boolean);
}

export default function FormRunner({ form, lang = "en", onSubmit, submitting, preview = false }) {
	const sections = form.sections || [];
	const layout = form.settings?.layout || "multiStep";
	const singlePage = layout === "singlePage" || sections.length <= 1;

	const [step, setStep] = useState(0);
	const [answers, setAnswers] = useState({});
	const [errors, setErrors] = useState({});
	const startedAtRef = useRef(new Date().toISOString());

	// --- answer-duration timer ---
	const limit = form.answerTimeLimitSeconds || 0;
	const [remaining, setRemaining] = useState(limit);
	useEffect(() => {
		if (!limit || preview) return undefined;
		const started = Date.now();
		const id = setInterval(() => {
			const left = Math.max(0, limit - Math.round((Date.now() - started) / 1000));
			setRemaining(left);
			if (left <= 0) {
				clearInterval(id);
				if (form.onTimerEnd === "autoSubmit") handleSubmit(true);
			}
		}, 1000);
		return () => clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [limit]);

	const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

	const validateStep = (sectionList) => {
		const errs = {};
		sectionList.forEach((sec) => {
			(sec.questions || []).forEach((q) => {
				if (LAYOUT_TYPES.includes(q.type)) return;
				if (!isVisible(q, answers)) return;
				const v = answers[q.key];
				const empty = v == null || v === "" || (Array.isArray(v) && v.length === 0);
				if (q.required && empty) errs[q.key] = tr("required", lang);
			});
		});
		setErrors(errs);
		return Object.keys(errs).length === 0;
	};

	const buildPayload = () => ({
		startedAt: startedAtRef.current,
		lang,
		answers: Object.entries(answers).map(([questionKey, value]) => ({ questionKey, value }))
	});

	const handleSubmit = (force = false) => {
		const all = sections;
		if (!force && !validateStep(all)) return;
		if (preview) return;
		onSubmit(buildPayload());
	};

	const visibleSections = singlePage ? sections : [sections[step]].filter(Boolean);
	const progress = sections.length ? Math.round(((step + 1) / sections.length) * 100) : 0;

	const renderSection = (sec, idx) => (
		<Card withBorder mb="md" key={sec._id || sec._local || idx} radius="md">
			{pickLocale(sec.title, lang) && <Title order={4} mb={4}>{pickLocale(sec.title, lang)}</Title>}
			{pickLocale(sec.description, lang) && <Text color="dimmed" size="sm" mb="sm">{pickLocale(sec.description, lang)}</Text>}
			<Stack>
				{(sec.questions || [])
					.filter((q) => isVisible(q, answers))
					.map((q) => (
						<QuestionRenderer
							key={q._id || q._local || q.key}
							question={q}
							lang={lang}
							value={answers[q.key]}
							error={errors[q.key]}
							onChange={(v) => setAnswer(q.key, v)}
						/>
					))}
			</Stack>
		</Card>
	);

	return (
		<Stack>
			<div>
				<Title order={2}>{pickLocale(form.title, lang)}</Title>
				{pickLocale(form.description, lang) && <Text color="dimmed">{pickLocale(form.description, lang)}</Text>}
				<Group mt="xs" spacing="xs">
					{form.estimatedMinutes ? <Badge variant="light">~{form.estimatedMinutes} {tr("minutes", lang)}</Badge> : null}
					{limit ? (
						<Badge color={remaining < 30 ? "red" : "blue"} leftSection={<IconClock size={12} />}>
							{remaining > 0 ? `${tr("timeLeft", lang)}: ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}` : tr("timeUp", lang)}
						</Badge>
					) : null}
				</Group>
			</div>

			{form.settings?.showProgress && !singlePage && <Progress value={progress} size="sm" radius="xl" />}

			{visibleSections.map(renderSection)}

			{preview && <Alert icon={<IconAlertCircle size={16} />} color="yellow">Preview mode — responses are not saved.</Alert>}

			<Group position="apart">
				{!singlePage && step > 0 ? (
					<Button variant="default" onClick={() => setStep((s) => s - 1)}>{tr("back", lang)}</Button>
				) : <span />}
				{!singlePage && step < sections.length - 1 ? (
					<Button onClick={() => { if (validateStep([sections[step]])) setStep((s) => s + 1); }}>{tr("next", lang)}</Button>
				) : (
					<Button loading={submitting} color="green" onClick={() => handleSubmit(false)}>{tr("submit", lang)}</Button>
				)}
			</Group>
		</Stack>
	);
}
